import React, { useState } from 'react';
import { 
  MapPin, Phone, Mail, Clock, Send, CheckCircle,
  Facebook, Instagram, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call (mock)
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    toast.success('Message sent successfully! We will get back to you soon.');
    
    // Reset form after delay
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
      });
    }, 3000);
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-32 bg-[#1B4F5D]">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <span className="text-[#408D4D] font-semibold tracking-wider uppercase text-sm">Get In Touch</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Have questions about our services? Need a quote? We're here to help. 
              Reach out to us today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-[#1B4F5D] mb-8">Contact Information</h2>
              
              <div className="space-y-6">
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-[#408D4D]/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="text-[#408D4D]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1B4F5D] mb-1">Office Address</h3>
                        <p className="text-gray-600">{companyInfo.address}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-[#408D4D]/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="text-[#408D4D]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1B4F5D] mb-1">Phone Number</h3>
                        <a href={`tel:${companyInfo.phone}`} className="text-gray-600 hover:text-[#408D4D] transition-colors">
                          {companyInfo.phone}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-[#408D4D]/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="text-[#408D4D]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1B4F5D] mb-1">Email Address</h3>
                        <a href={`mailto:${companyInfo.email}`} className="text-gray-600 hover:text-[#408D4D] transition-colors">
                          {companyInfo.email}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-[#408D4D]/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="text-[#408D4D]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1B4F5D] mb-1">Business Hours</h3>
                        <p className="text-gray-600">Monday - Friday</p>
                        <p className="text-gray-600">8:00 AM - 5:00 PM</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Social Links */}
              <div className="mt-8">
                <h3 className="font-semibold text-[#1B4F5D] mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a 
                    href={companyInfo.social.facebook} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-lg bg-[#1B4F5D] flex items-center justify-center text-white hover:bg-[#408D4D] transition-colors"
                  >
                    <Facebook size={20} />
                  </a>
                  <a 
                    href={companyInfo.social.instagram} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-lg bg-[#1B4F5D] flex items-center justify-center text-white hover:bg-[#408D4D] transition-colors"
                  >
                    <Instagram size={20} />
                  </a>
                  <a 
                    href={companyInfo.social.tiktok} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-lg bg-[#1B4F5D] flex items-center justify-center text-white hover:bg-[#408D4D] transition-colors"
                  >
                    <MessageCircle size={20} />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8 md:p-10">
                  <h2 className="text-2xl font-bold text-[#1B4F5D] mb-2">Send Us a Message</h2>
                  <p className="text-gray-600 mb-8">Fill out the form below and we'll get back to you shortly.</p>
                  
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 mx-auto rounded-full bg-[#408D4D]/10 flex items-center justify-center mb-6">
                        <CheckCircle className="text-[#408D4D]" size={40} />
                      </div>
                      <h3 className="text-2xl font-bold text-[#1B4F5D] mb-3">Message Sent!</h3>
                      <p className="text-gray-600">
                        Thank you for reaching out. We'll respond to your inquiry within 24 hours.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-[#1B4F5D]">Full Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            type="text"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="John Doe"
                            className="h-12 border-gray-200 focus:border-[#408D4D] focus:ring-[#408D4D]"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-[#1B4F5D]">Email Address *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="john@example.com"
                            className="h-12 border-gray-200 focus:border-[#408D4D] focus:ring-[#408D4D]"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-[#1B4F5D]">Phone Number</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+233 XX XXX XXXX"
                            className="h-12 border-gray-200 focus:border-[#408D4D] focus:ring-[#408D4D]"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="company" className="text-[#1B4F5D]">Company Name</Label>
                          <Input
                            id="company"
                            name="company"
                            type="text"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder="Your Company"
                            className="h-12 border-gray-200 focus:border-[#408D4D] focus:ring-[#408D4D]"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject" className="text-[#1B4F5D]">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          type="text"
                          required
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="How can we help you?"
                          className="h-12 border-gray-200 focus:border-[#408D4D] focus:ring-[#408D4D]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-[#1B4F5D]">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          required
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your shipment needs..."
                          rows={5}
                          className="border-gray-200 focus:border-[#408D4D] focus:ring-[#408D4D] resize-none"
                        />
                      </div>

                      <Button 
                        type="submit" 
                        disabled={isSubmitting}
                        className="w-full bg-[#408D4D] hover:bg-[#2D6636] text-white h-14 text-lg rounded-lg font-semibold transition-all duration-300"
                      >
                        {isSubmitting ? (
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Sending...
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Send size={20} />
                            Send Message
                          </div>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-[400px] bg-gray-200 relative">
        <iframe
          title="CitiBiz Connections Location"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d31772.26776846887!2d0.00543!3d5.6783!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xfdf9046c38c1ad5%3A0xc8e3c0da96c6ada0!2sTema!5e0!3m2!1sen!2sgh!4v1628345678901!5m2!1sen!2sgh"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </section>
    </div>
  );
};

export default ContactPage;
